import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  fetchInventoryRules,
  TInventoryRulesOrderByKey,
} from '~/src/dash/apiV2/inventory-rules/inventory-rules';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { formatSelectedFiltersIntoQueryObject } from '~/src/dash/components/filters/conversions';
import {
  getComparator,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import { isSameMoment } from '~/src/dash/utils/calendar';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { validateDateRanges } from '~/src/dash/components/calendar/common';

import { AllRules } from '~/src/dash/views/inventory-rules/AllRules';
import { AdBreakDrawer } from '~/src/dash/views/inventory-rules/AllRules/AdBreakDrawer';
import { adBreakTableConfig } from '~/src/dash/views/inventory-rules/AllRules/AdBreakDrawer/config';
import {
  allRulesViewConfig,
  formatDataToLVTRows,
} from '~/src/dash/views/inventory-rules/AllRules/config';
import {
  IInventoryRules,
  IInventoryRulesResponse,
  IInventoryRulesUiParams,
} from '~/src/dash/views/inventory-rules/common';
import { IAdBreak } from '~/src/dash/views/inventory-rules/common';
import { formatDrawerDataIntoLVTRows } from '~/src/dash/views/inventory-rules/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { TKey, usePublicaTranslation } from '~/src/locales/en';

export const AllRulesContainer = () => {
  const isMounted = useRef(false);
  const { session, user, isSuperUser } = useBaseStore();
  const { t } = usePublicaTranslation();
  const cfg = allRulesViewConfig;
  const adBreakCfg = adBreakTableConfig;
  const hiddenDrawerOverviewFields = ['AdBreaks', 'CreatedAt'];
  const [inventoryRules, setInventoryRules] = useState<IInventoryRules[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [snackbarError, setSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [isActionPending, setIsActionPending] = useState(false);
  const [filters, setFilters] = useState<Record<string, ISelectOption[]>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string | number>>>({});
  const [filtersLoadState, setFiltersLoadState] = useState<LoadingState>(LoadingStateNoData);
  const [startToEndDateRanges, setStartToEndDateRanges] = useState<IStartToEndDateRanges>(
    cfg.startToEndDateRanges,
  );
  const [areDateRangesInvalid, setAreDateRangesInvalid] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [order, setOrder] = useState<Order>(cfg.defaultState.orderDirection || 'asc');
  const [orderBy, setOrderBy] = useState<TInventoryRulesOrderByKey>(cfg.defaultState.orderBy || '');
  const [page, setPage] = useState(cfg.defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(cfg.defaultState.rowsPerPage);
  const [sortedRows, setSortedRows] = useState([] as IListViewTableRow[]);

  // drawer states
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerOverview, setDrawerOverview] = useState<Record<string, string>>({});
  const [drawerTime, setDrawerTime] = useState<Record<string, string>>({});
  const [drawerContent, setDrawerContent] = useState<Record<string, string>>({});
  const [drawerCustom, setDrawerCustom] = useState<Record<string, string>>({});
  const [drawerTableData, setDrawerTableData] = useState<IAdBreak[]>([]);
  const [drawerLoadState, setDrawerLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [drawerOrder, setDrawerOrder] = useState<Order>(
    adBreakCfg.defaultState.orderDirection || 'asc',
  );
  const [drawerOrderBy, setDrawerOrderBy] = useState<string>(adBreakCfg.defaultState.orderBy || '');
  const [drawerPage, setDrawerPage] = useState(adBreakCfg.defaultState.page || 0);
  const [drawerRowsPerPage, setDrawerRowsPerPage] = useState<RowsPerPage>(
    adBreakCfg.defaultState.rowsPerPage,
  );
  const [sortedDrawerRows, setSortedDrawerRows] = useState([] as IListViewTableRow[]);

  const getData = (): void => {
    const startTime = moment().unix();
    setLoadState(LoadingStateLoading);
    const filterParams = formatSelectedFiltersIntoQueryObject(selectedFilters, cfg.filters);
    void fetchInventoryRules(session.publisher.ID, {
      PageSize: rowsPerPage,
      Page: page,
      OrderDirection: order,
      OrderBy: orderBy,
      Timezone: session.publisher.Timezone,
      StartDateFrom: startToEndDateRanges.startDateFrom?.format('YYYY-MM-DD') || '',
      StartDateTo: startToEndDateRanges.startDateTo?.format('YYYY-MM-DD') || '',
      EndDateFrom: startToEndDateRanges.endDateFrom?.format('YYYY-MM-DD') || '',
      EndDateTo: startToEndDateRanges.endDateTo?.format('YYYY-MM-DD') || '',
      Search: searchInput.trim() || '', // Server-side search
      ...filterParams,
    })
      .then(({ InventoryRules, TotalRows }: IInventoryRulesResponse) => {
        const loadTime = moment().unix() - startTime;
        track(
          'Inventory Rules - List Displayed',
          {
            isSuperUser,
            loadTime,
          },
          user.user,
        );
        unstable_batchedUpdates(() => {
          setInventoryRules(InventoryRules || []);
          setTotalRows(TotalRows);
          setLoadState(LoadingStateHasData);
          setIsActionPending(false);
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setSnackbarError(true);
          setSnackbarMessage(t('SnackbarErrorRetrievingData'));
          setTotalRows(0);
          setLoadState(LoadingStateNoData);
          setIsActionPending(false);
        });
      });
  };

  // update config for state persistence
  const updateCfg = (params: IInventoryRulesUiParams): void => {
    if (params.currPage && cfg.defaultState) cfg.defaultState.page = params.currPage;
    if (params.rowsPerPage && cfg.defaultState) cfg.defaultState.rowsPerPage = params.rowsPerPage;
  };

  const handleClickOpenDrawer = (row: IListViewTableRow): void => {
    const overview = {} as Record<string, string>;
    const time = {} as Record<string, string>;
    const content = {} as Record<string, string>;
    const custom = {} as Record<string, string>;
    for (const [key, valueObj] of Object.entries(row)) {
      if (!isEmpty(valueObj.value) && !hiddenDrawerOverviewFields.includes(key)) {
        let tKey = t(key as TKey);
        // format type set as Name column in config because Name column is required
        // this converts the key to Format Type for the overview display
        if (key === 'Name') {
          tKey = t('FormatType');
        }
        // ID included to help navigate between rules in drawer but not displayed
        if (['ID', 'Name', 'Key', 'Value'].includes(key)) {
          overview[tKey] = String(valueObj.value) || '';
        } else if (['StartTime', 'EndTime'].includes(key)) {
          time[tKey] = String(valueObj.value) || '';
        } else if (key.includes('Content')) {
          content[tKey] = String(valueObj.value) || '';
        } else if (key.includes('Custom')) {
          custom[tKey] = String(valueObj.value) || '';
        }
      }
    }

    const adBreaksValue = row.AdBreaks?.value as string[] | [];
    const adBreaks: IAdBreak[] = (adBreaksValue || []).map((adBreak: string) => {
      const parts = adBreak.split(',');
      return {
        AdBreakID: parts[0] || 0,
        PodDuration: parts[2] || 0,
        SlotCount: parts[3] || 0,
        MinAdDuration: parts[4] || 'N/A',
        MaxAdDuration: parts[5] || 'N/A',
        CuePointTypes: parts[6] || 'N/A',
        CuePointValue: parts[7] || 'N/A',
      } as IAdBreak;
    });
    unstable_batchedUpdates(() => {
      setDrawerTableData(adBreaks);
      setDrawerOverview(overview);
      setDrawerTime(time);
      setDrawerContent(content);
      setDrawerCustom(custom);
      setIsDrawerOpen(true);
      setDrawerLoadState(LoadingStateHasData);
    });
  };

  const populateFilters = (): void => {
    setFiltersLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];

    Object.values(cfg.filters).forEach((filter) => {
      keys.push(filter.key);
      getListFns.push(filter.getList(session, user));
    });

    const tempFilters: Record<string, ISelectOption[]> = {};
    const tempSelectedFilters: Record<string, Set<string | number>> = {};

    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, index) => {
          const filterKey = keys[index];
          tempFilters[filterKey] = result;
          tempSelectedFilters[filterKey] =
            cfg.filters[filterKey].selectedOptionIDs || new Set<string | number>();
        });
        unstable_batchedUpdates(() => {
          setFilters(tempFilters);
          setFiltersLoadState(LoadingStateHasData);
          setSelectedFilters(tempSelectedFilters);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setFilters({});
          setSelectedFilters({});
          setFiltersLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage(t('SnackbarErrorRetrievingItem', { item: t('Filters') }));
        });
      });
  };

  // filter functions
  const handleFilterAccept = (value: Set<number | string>, key: string): void => {
    if (
      selectedFilters[key].size === value.size &&
      [...selectedFilters[key]].every((item) => value.has(item))
    ) {
      // No change, do nothing
      return;
    } else {
      const clonedSelectedFilters = { ...selectedFilters };
      clonedSelectedFilters[key] = value;
      setSelectedFilters(clonedSelectedFilters);
      cfg.filters[key].selectedOptionIDs = value;
      if (value.size) {
        track('Inventory Rules - Filter', { isSuperUser, session, filter: key }, user.user);
      }
    }
  };

  const handleClearFilters = (): void => {
    const clearedSelectedFilters: Record<string, Set<string | number>> = {};
    Object.values(cfg.filters).forEach((filter) => {
      clearedSelectedFilters[filter.key] = new Set<string | number>(
        filter.selectedOptionIDsOnReset || [],
      );
    });
    setSelectedFilters(clearedSelectedFilters);
  };

  const handleApplyDateRanges = (ranges: IStartToEndDateRanges): void => {
    const { startDateFrom, startDateTo, endDateFrom, endDateTo } = ranges;
    // Only update state if the values are different
    const hasChanged =
      !isSameMoment(startToEndDateRanges.startDateFrom, startDateFrom) ||
      !isSameMoment(startToEndDateRanges.startDateTo, startDateTo) ||
      !isSameMoment(startToEndDateRanges.endDateFrom, endDateFrom) ||
      !isSameMoment(startToEndDateRanges.endDateTo, endDateTo);

    if (hasChanged) {
      // validate date ranges
      const { isValid, errorTKey, errorTProps } = validateDateRanges(ranges);
      if (!isValid) {
        unstable_batchedUpdates(() => {
          setSnackbarMessage(t(errorTKey, errorTProps));
          setSnackbarError(true);
          setAreDateRangesInvalid(true);
        });
      } else {
        unstable_batchedUpdates(() => {
          setStartToEndDateRanges(ranges);
          setAreDateRangesInvalid(false);
          setPage(0);
        });
        cfg.startToEndDateRanges = ranges;
      }
    } else if (!startDateFrom && !startDateTo && !endDateFrom && !endDateTo) {
      setAreDateRangesInvalid(false);
    }
  };

  const handleOnChangeSearch = (text: string): void => {
    setSearchInput(text);
  };

  // update page number
  const handleChangePage = (_event: React.MouseEvent | null, page: number): void => {
    setPage(page);
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
  };

  const handleDrawerChangePage = (_e: unknown, newPage: number): void => {
    setDrawerPage(newPage);
  };

  const handleDrawerChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setDrawerRowsPerPage(newRowsPerPage);
      setDrawerPage(0);
    });
  };

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;

    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key as TInventoryRulesOrderByKey);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleOnClickDrawerColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;

    if (drawerOrderBy !== key) {
      unstable_batchedUpdates(() => {
        setDrawerOrder(defaultOrderDirection || 'asc');
        setDrawerOrderBy(key);
        setDrawerPage(0);
      });
    } else {
      const isAsc = drawerOrderBy === key && drawerOrder === 'asc';
      unstable_batchedUpdates(() => {
        setDrawerOrder(isAsc ? 'desc' : 'asc');
        setDrawerOrderBy(key);
        setDrawerPage(0);
      });
    }
  };

  // for drawer
  function handleClickPrev(index: number): void {
    if (index > 0 && index < sortedRows.length) {
      const prevLVTRow = [sortedRows[index - 1]] as IListViewTableRow[];
      handleClickOpenDrawer(prevLVTRow[0]);
    }
  }

  // for drawer
  function handleClickNext(index: number): void {
    if (index >= 0 && index < sortedRows.length - 1) {
      const nextLVTRow = [sortedRows[index + 1]] as IListViewTableRow[];
      handleClickOpenDrawer(nextLVTRow[0]);
    }
  }

  useEffect(() => {
    populateFilters();
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      getData(); // Server handles search + pagination + filtering
    }
  }, [order, orderBy, page, rowsPerPage, startToEndDateRanges, selectedFilters, searchInput]);

  useEffect(() => {
    isMounted.current = true;
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      cfg.defaultState.page = page;
      cfg.defaultState.orderDirection = order;
      cfg.defaultState.orderBy = orderBy;
    } else {
      isMounted.current = true;
    }
  }, [page, order, orderBy]);

  useEffect(() => {
    if (isMounted.current) {
      if (!isEmpty(inventoryRules)) {
        const rows = formatDataToLVTRows(inventoryRules, cfg.table);
        setSortedRows(
          stableSort(rows, getComparator(order, orderBy, cfg.table.columns[orderBy].valueType)),
        );
      } else {
        setSortedRows([]);
      }
    }
  }, [order, orderBy, inventoryRules]);

  useEffect(() => {
    if (isMounted.current) {
      setPage(0); // Reset page when search input changes
    }
  }, [searchInput]);

  // drawer useEffects
  useEffect(() => {
    if (isMounted.current) {
      adBreakCfg.defaultState.page = drawerPage;
      adBreakCfg.defaultState.orderDirection = drawerOrder;
      adBreakCfg.defaultState.orderBy = drawerOrderBy;
    } else {
      isMounted.current = true;
    }
  }, [drawerPage, drawerOrder, drawerOrderBy]);

  useEffect(() => {
    if (isMounted.current) {
      if (!isEmpty(inventoryRules)) {
        setSortedDrawerRows(
          stableSort(
            drawerRows,
            getComparator(
              drawerOrder,
              drawerOrderBy,
              adBreakCfg.table.columns[drawerOrderBy].valueType,
            ),
          ),
        );
      } else {
        setSortedDrawerRows([]);
      }
    }
  }, [drawerOrder, drawerOrderBy, drawerTableData]);

  // Server handles all pagination and search - no client-side slicing needed
  const visibleRows = sortedRows;
  const drawerRows = formatDrawerDataIntoLVTRows(drawerTableData, adBreakCfg);
  const visibleDrawerRows = sortedDrawerRows.slice(
    drawerPage * drawerRowsPerPage,
    drawerPage * drawerRowsPerPage + drawerRowsPerPage,
  );

  return (
    <>
      <AllRules
        fetchInventoryRules={getData}
        cfg={cfg}
        updateCfg={updateCfg}
        isActionPending={isActionPending}
        inventoryRules={inventoryRules}
        visibleRows={visibleRows}
        totalRows={totalRows}
        loadState={loadState}
        handleClickOpenDrawer={handleClickOpenDrawer}
        handleFilterAccept={handleFilterAccept}
        handleClearFilters={handleClearFilters}
        filters={filters}
        selectedFilters={selectedFilters}
        filtersLoadState={filtersLoadState}
        handleApplyDateRanges={handleApplyDateRanges}
        startToEndDateRanges={startToEndDateRanges}
        areDateRangesInvalid={areDateRangesInvalid}
        handleOnChangeSearch={handleOnChangeSearch}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleOnClickColumnHeader={handleOnClickColumnHeader}
        orderBy={orderBy}
        order={order}
        page={page}
        rowsPerPage={rowsPerPage}
      />
      <AdBreakDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        drawerOverview={drawerOverview}
        drawerTime={drawerTime}
        drawerContent={drawerContent}
        drawerCustom={drawerCustom}
        drawerTableData={drawerTableData}
        sortedRows={sortedRows}
        drawerLoadState={drawerLoadState}
        handleClickNext={handleClickNext}
        handleClickPrev={handleClickPrev}
        handleOnClickColumnHeader={handleOnClickDrawerColumnHeader}
        visibleDrawerRows={visibleDrawerRows}
        order={drawerOrder}
        orderBy={drawerOrderBy}
        page={drawerPage}
        rowsPerPage={drawerRowsPerPage}
        handleChangePage={handleDrawerChangePage}
        handleChangeRowsPerPage={handleDrawerChangeRowsPerPage}
      />
      <SnackbarView
        open={!!snackbarMessage}
        error={snackbarError}
        onClose={() => setSnackbarMessage('')}
      >
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
